import { normalizeCombo } from "@/domain/profile-resolver/index";
import type { Layers } from "@/domain/types";

/** One position a Command occupies: a key/zone code plus its modifier combo ("" = base). */
export interface DiffPosition {
  code: string;
  combo: string;
}

export type DiffStatus = "moved" | "added" | "removed";

/**
 * One changed Command in the Changes list. `a` and `b` are the Command's full
 * position set on each side (Alternates included, never collapsed); an empty array
 * means the Command is unbound on that side.
 */
export interface DiffRow {
  id: string;
  name: string;
  category: string;
  status: DiffStatus;
  a: DiffPosition[];
  b: DiffPosition[];
}

/** The compare result: the listed changes plus a count of identically-placed Commands. */
export interface DiffResult {
  rows: DiffRow[];
  same: number;
}

interface Collected {
  id: string;
  name: string;
  category: string;
  positions: DiffPosition[];
}

const STATUS_ORDER: Record<DiffStatus, number> = { moved: 0, added: 1, removed: 2 };

/** A Command's stable identity for the diff: its command id, or category:name where absent. */
function identity(binding: { cmd?: string; category: string; name: string }): string {
  return binding.cmd ?? `${binding.category}:${binding.name}`;
}

/** Order-independent key for a single position, for set membership. */
function posKey(p: DiffPosition): string {
  return `${p.code}|${p.combo}`;
}

/** Positions in a stable display order: base layer first, then by combo, then by code. */
function sortPositions(positions: DiffPosition[]): DiffPosition[] {
  return [...positions].sort(
    (x, y) => x.combo.localeCompare(y.combo) || x.code.localeCompare(y.code),
  );
}

/** True when two sides occupy the same set of positions, regardless of order. */
function samePositions(a: DiffPosition[], b: DiffPosition[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(b.map(posKey));
  return a.every((p) => set.has(posKey(p)));
}

/**
 * Gather one entry per Command across all layers, accumulating the full set of
 * positions it occupies (base under combo "", each modifier layer under its
 * canonical combo). Name and Category are taken from the first binding seen.
 */
function collect(layers: Layers): Map<string, Collected> {
  const byId = new Map<string, Collected>();
  const put = (
    combo: string,
    code: string,
    binding: { cmd?: string; category: string; name: string },
  ) => {
    const id = identity(binding);
    let entry = byId.get(id);
    if (!entry) {
      entry = { id, name: binding.name, category: binding.category, positions: [] };
      byId.set(id, entry);
    }
    entry.positions.push({ code, combo });
  };
  for (const [code, binding] of Object.entries(layers.base)) put("", code, binding);
  for (const [combo, layer] of Object.entries(layers.mods ?? {})) {
    const canonical = normalizeCombo(combo);
    for (const [code, binding] of Object.entries(layer)) put(canonical, code, binding);
  }
  return byId;
}

/**
 * Diff two resolved Profiles: match Commands by id (cmd, falling back to
 * category:name) and compare the set of positions each occupies. A Command on both
 * sides at an identical position set is `same` (counted, not listed); a different set
 * is `moved`; B-only is `added`; A-only is `removed`. Pure over two `Layers`: no
 * Keyboard or Keyboard Layout participates.
 */
export function computeDiff(a: Layers, b: Layers): DiffResult {
  const A = collect(a);
  const B = collect(b);
  const rows: DiffRow[] = [];
  let same = 0;

  for (const [id, ae] of A) {
    const be = B.get(id);
    if (!be) {
      rows.push({
        id,
        name: ae.name,
        category: ae.category,
        status: "removed",
        a: sortPositions(ae.positions),
        b: [],
      });
    } else if (samePositions(ae.positions, be.positions)) {
      same++;
    } else {
      rows.push({
        id,
        name: ae.name,
        category: ae.category,
        status: "moved",
        a: sortPositions(ae.positions),
        b: sortPositions(be.positions),
      });
    }
  }
  for (const [id, be] of B) {
    if (!A.has(id)) {
      rows.push({
        id,
        name: be.name,
        category: be.category,
        status: "added",
        a: [],
        b: sortPositions(be.positions),
      });
    }
  }

  rows.sort(
    (x, y) => STATUS_ORDER[x.status] - STATUS_ORDER[y.status] || x.name.localeCompare(y.name),
  );
  return { rows, same };
}
